// queue-engine.js — the Live OPD Queue Engine PURE CORE (docs/opd/LIVE_QUEUE_ENGINE.md).
//
// Dependency-free, deterministic, no I/O. This is the single source of truth for
// FOUR families of pure functions the browser screens use AND (copied verbatim,
// parity-tested like intake-guards) the anon-saathi-finalize Bot uses:
//
//   PRESENCE   derivePresence(role, recentActivity) — §3. Duty is DERIVED at READ
//              time from the record: an explicit fresh on/off toggle wins, else a
//              consult transition authored within the last 30 min counts as ON,
//              else OFF. No heartbeats, no cron, no ghost states after a restart —
//              a stale toggle self-expires (DUTY_MAX_AGE_MS), so presence is always
//              truthful about "right now" without any background process.
//
//   ORDERING   queueOrder(entries) — §1+§2. A token is a DEPARTMENT queue position,
//              not a doctor lock. Order = priority class (stat → referred → normal)
//              then effective check-in instant. A referral keeps its ORIGINAL
//              check-in instant (effectiveSince) so it slots by when the patient
//              first stood in line, never at the back.
//
//   ESTIMATES  serviceRate(completedRecent, presentCount) + estimateWait(position,
//              rate) — §6. Self-correcting: the department's measured pace over the
//              trailing window drives every waiting patient's estimate, so a fast
//              doctor finishing quickly drops everyone's estimate on the next tick.
//              No stored estimates — always derived from the record + presence.
//
//   CLASSES    readQueueClass / writeQueueClass — the opd/queue-class extension
//              {class, effectiveSince} read/write helpers (pure; the WRITE returns
//              a new extension array, the caller performs the FHIR update).
//
// Everything here is pure so a Bot lambda and the SPA agree byte-for-byte.

// ── FHIR extension URLs (the https://healther.io/opd/* family) ───────────────
export const OPD_QUEUE_CLASS_EXT = 'https://healther.io/opd/queue-class';
export const OPD_ON_DUTY_EXT = 'https://healther.io/opd/on-duty';

// ── Priority classes (§1) — lower rank is served first ───────────────────────
export const QUEUE_CLASS = Object.freeze({ STAT: 'stat', REFERRED: 'referred', NORMAL: 'normal' });
export const QUEUE_CLASS_RANK = Object.freeze({ stat: 0, referred: 1, normal: 2 });
export const DEFAULT_QUEUE_CLASS = QUEUE_CLASS.NORMAL;

// ── Presence (§3) ────────────────────────────────────────────────────────────
// Consult activity within this window counts as "present" without a toggle.
export const PRESENCE_WINDOW_MS = 30 * 60 * 1000; // 30 min
// An explicit on/off toggle older than this is IGNORED (self-expiring → no ghost
// "on" state survives a restart or an overnight gap). ~16h covers one clinic day.
export const DUTY_MAX_AGE_MS = 16 * 60 * 60 * 1000;

// ── Estimates (§6) ───────────────────────────────────────────────────────────
export const SERVICE_WINDOW_MIN = 75; // trailing window the pace is measured over
export const DEFAULT_CONSULT_MIN = 12; // fallback per-consult minutes (no measured pace yet)
export const MIN_RATE_MIN = 2; // a per-position increment never dips below this
export const MAX_RATE_MIN = 45; // …nor climbs above this (a stalled dept still reads sanely)
export const MAX_WAIT_MIN = 240; // cap a single estimate so a huge queue can't print "~9h"

const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : Number(v));
const toMs = (v) => {
  if (v == null) return NaN;
  if (typeof v === 'number') return Number.isFinite(v) ? v : NaN;
  const t = Date.parse(String(v));
  return Number.isFinite(t) ? t : NaN;
};

// ═════════════════════════════════════════════════════════════════════════════
// PRESENCE — §3 duty is derived + explicit, no heartbeat infrastructure.
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Read the opd/on-duty extension off a PractitionerRole (or a plain object
 * carrying an `extension` array), or null when absent/blank.
 * @returns {{ status:'on'|'off', room:(string|null), since:(string|null) }|null}
 */
export function readOnDuty(role) {
  const ext = ((role && role.extension) || []).find((e) => e && e.url === OPD_ON_DUTY_EXT);
  if (!ext || typeof ext.valueString !== 'string') return null;
  let v;
  try { v = JSON.parse(ext.valueString); } catch (_) { return null; }
  if (!v || (v.status !== 'on' && v.status !== 'off')) return null;
  return {
    status: v.status,
    room: v.room != null && v.room !== '' ? String(v.room) : null,
    since: v.since ? String(v.since) : null,
  };
}

/**
 * Build the opd/on-duty extension value JSON for a WRITE (pure). The Bot stamps
 * this onto PractitionerRole; `since` is the toggle instant so read-time freshness
 * can self-expire it.
 */
export function buildOnDutyExtension({ status, room, since } = {}) {
  const st = status === 'on' ? 'on' : 'off';
  const payload = { status: st, since: since || new Date().toISOString() };
  if (room != null && String(room).trim() !== '') payload.room = String(room).trim();
  return { url: OPD_ON_DUTY_EXT, valueString: JSON.stringify(payload) };
}

/** True iff an explicit toggle is recent enough to still be authoritative. */
function dutyFresh(duty, now, maxAgeMs) {
  if (!duty || !duty.since) return false;
  const s = toMs(duty.since);
  if (!Number.isFinite(s)) return false;
  return now - s >= 0 && now - s <= maxAgeMs;
}

/**
 * derivePresence(role, recentActivity, opts) — the ONE pure presence function (§3).
 *
 * Precedence (computed at READ time from the record, restart-safe):
 *   1. A FRESH explicit toggle (on/off, `since` within DUTY_MAX_AGE_MS) is
 *      authoritative — "I'm in" / "I'm out" the doctor set today.
 *   2. Else DERIVED: a consult transition this doctor authored within the last
 *      30 min ⇒ ON (they are clearly here even without a toggle).
 *   3. Else OFF (a stale toggle is treated as absent → no ghost "on" state).
 *
 * @param {object} role  the doctor's PractitionerRole (carries the on-duty ext + room)
 * @param {object} recentActivity  { lastActivityMs?:number } — epoch ms of the most
 *        recent Encounter state transition this doctor authored (see the read-spec
 *        note below). Absent/NaN ⇒ no derived presence.
 * @param {object} [opts]  { now?, windowMs?, maxAgeMs? } (test seams)
 * @returns {{ status:'on'|'off', room:(string|null), since:(string|null),
 *             source:'explicit'|'derived'|'none', derived:boolean }}
 *
 * ── RECENT-ACTIVITY READ SPEC (the cheapest honest signal) ───────────────────
 * "A consult transition authored by this practitioner within 30 min" is read as
 * Encounter.meta.lastUpdated on any Encounter that (a) has this doctor as a
 * participant.individual and (b) is in a post-call state (CALLED / IN_CONSULT /
 * PRESCRIBED / COMPLETED). We use meta.lastUpdated — which Medplum bumps on EVERY
 * write — rather than minting/scanning Provenance, because the state-transition
 * writes (medplum-callin flip, the CALLED stamp, sign-visit) ALREADY touch the
 * Encounter, so the signal is free and already in the record. The caller
 * (medplum-duty.js recentConsultActivity) runs one bounded Encounter search
 * scoped by participant=%profile and takes the max lastUpdated; no new resource,
 * no cron. Documented here so the lib stays the single source of the rule.
 */
export function derivePresence(role, recentActivity = {}, opts = {}) {
  const now = num(opts.now) || Date.now();
  const windowMs = num(opts.windowMs) || PRESENCE_WINDOW_MS;
  const maxAgeMs = num(opts.maxAgeMs) || DUTY_MAX_AGE_MS;
  const duty = readOnDuty(role);
  const room = (duty && duty.room) || roomFromRole(role);

  if (duty && dutyFresh(duty, now, maxAgeMs)) {
    return { status: duty.status, room, since: duty.since, source: 'explicit', derived: false };
  }

  const lastMs = toMs(recentActivity.lastActivityMs);
  const activeRecently = Number.isFinite(lastMs) && now - lastMs >= 0 && now - lastMs <= windowMs;
  if (activeRecently) {
    return { status: 'on', room, since: new Date(lastMs).toISOString(), source: 'derived', derived: true };
  }
  return { status: 'off', room, since: null, source: 'none', derived: false };
}

/** PractitionerRole.location[0].display (the doctor's seat), or null. */
export function roomFromRole(role) {
  const loc = (role && Array.isArray(role.location) ? role.location[0] : null);
  const disp = loc && (loc.display || (loc.reference ? null : null));
  return disp ? String(disp) : null;
}

/**
 * presentDoctorIds(roles, activityByDoctor, opts) — the present-doctor SET for a
 * department: every role whose derivePresence is 'on'. `activityByDoctor` maps a
 * bare Practitioner UUID → { lastActivityMs }. Pure. Returns a Set of ids.
 */
export function presentDoctorIds(roles, activityByDoctor = {}, opts = {}) {
  const out = new Set();
  for (const role of Array.isArray(roles) ? roles : []) {
    const pid = practitionerIdOf(role);
    if (!pid) continue;
    const act = activityByDoctor[pid] || {};
    if (derivePresence(role, act, opts).status === 'on') out.add(pid);
  }
  return out;
}

function practitionerIdOf(role) {
  const ref = role && role.practitioner && role.practitioner.reference;
  return ref && String(ref).indexOf('Practitioner/') === 0 ? String(ref).split('/')[1] : (role && role.practitionerId) || null;
}

// ═════════════════════════════════════════════════════════════════════════════
// CLASSES — §1+§2 the opd/queue-class {class, effectiveSince} extension.
// ═════════════════════════════════════════════════════════════════════════════

/**
 * readQueueClass(enc) → { class, effectiveSince } for an Encounter. Defaults:
 *   class          = 'normal'
 *   effectiveSince = the check-in instant (Encounter.period.start) — so a walk-in
 *                    with no explicit class still orders by when they arrived.
 * A referral carries class 'referred' + effectiveSince = the patient's ORIGINAL
 * check-in instant (stamped when the referral re-queues them — §2).
 */
export function readQueueClass(enc) {
  const ext = ((enc && enc.extension) || []).find((e) => e && e.url === OPD_QUEUE_CLASS_EXT);
  const fallbackSince = (enc && enc.period && enc.period.start) || null;
  if (ext && typeof ext.valueString === 'string') {
    try {
      const v = JSON.parse(ext.valueString) || {};
      const cls = QUEUE_CLASS_RANK[v.class] != null ? v.class : DEFAULT_QUEUE_CLASS;
      return { class: cls, effectiveSince: v.effectiveSince || fallbackSince };
    } catch (_) { /* fall through to default */ }
  }
  return { class: DEFAULT_QUEUE_CLASS, effectiveSince: fallbackSince };
}

/** Build the opd/queue-class extension value for a WRITE (pure). */
export function buildQueueClassExtension({ queueClass, effectiveSince } = {}) {
  const cls = QUEUE_CLASS_RANK[queueClass] != null ? queueClass : DEFAULT_QUEUE_CLASS;
  const payload = { class: cls };
  if (effectiveSince) payload.effectiveSince = String(effectiveSince);
  return { url: OPD_QUEUE_CLASS_EXT, valueString: JSON.stringify(payload) };
}

/**
 * queueOrder(entries) — §1+§2 the ONE ordering function. Sorts a list of queue
 * entries by (1) priority class rank, (2) effective check-in instant (earliest
 * first), (3) token as a stable final tiebreak. PURE — returns a NEW array; the
 * input is untouched.
 *
 * @param {Array<{ queueClass?:string, effectiveSince?:(string|number),
 *                 token?:string, timeIso?:string, period?:object }>} entries
 * Each entry may carry `queueClass`/`effectiveSince` directly (the shaped row) OR
 * an `extension`/`period` (a raw Encounter) — readQueueClass is applied when the
 * shaped fields are absent, so both shapes sort correctly.
 */
export function queueOrder(entries) {
  const rows = (Array.isArray(entries) ? entries : []).map((e, i) => {
    let cls = e && e.queueClass;
    let since = e && e.effectiveSince;
    if (cls == null || since == null) {
      const qc = readQueueClass(e);
      if (cls == null) cls = qc.class;
      if (since == null) since = qc.effectiveSince;
    }
    return {
      _e: e,
      _i: i,
      rank: QUEUE_CLASS_RANK[cls] != null ? QUEUE_CLASS_RANK[cls] : QUEUE_CLASS_RANK.normal,
      since: toMs(since),
      token: String((e && (e.token || (tokenExtOf(e)))) || ''),
    };
  });
  rows.sort((a, b) => (
    a.rank - b.rank
    || cmpSince(a.since, b.since)
    || String(a.token).localeCompare(String(b.token), undefined, { numeric: true })
    || a._i - b._i
  ));
  return rows.map((r) => r._e);
}

// A NaN effectiveSince sorts AFTER a real instant (an undateable row goes last
// within its class), never crashing the comparator.
function cmpSince(a, b) {
  const aok = Number.isFinite(a);
  const bok = Number.isFinite(b);
  if (aok && bok) return a - b;
  if (aok) return -1;
  if (bok) return 1;
  return 0;
}
function tokenExtOf(e) {
  const ext = ((e && e.extension) || []).find((x) => x && x.url === 'https://healther.io/opd/token');
  return ext ? ext.valueString : '';
}

// ═════════════════════════════════════════════════════════════════════════════
// ESTIMATES — §6 wait estimates that self-correct.
// ═════════════════════════════════════════════════════════════════════════════

function clampRate(r) {
  if (!Number.isFinite(r) || r <= 0) return DEFAULT_CONSULT_MIN;
  return Math.min(MAX_RATE_MIN, Math.max(MIN_RATE_MIN, r));
}
function clampWait(w) {
  if (!Number.isFinite(w) || w < 0) return 0;
  return Math.min(MAX_WAIT_MIN, Math.round(w));
}

/**
 * serviceRate(completedRecent, presentCount, opts) — §6. The per-QUEUE-POSITION
 * wait increment (minutes), self-correcting.
 *
 * MEASURED pace: the department completed `completedRecent` consults across
 * `presentCount` present doctors in the trailing SERVICE_WINDOW_MIN. With the
 * doctors serving the single department line in parallel, the wait a patient adds
 * per position ahead of them is windowMin / completedRecent — the present-doctor
 * count already shows up inside `completedRecent` (more/faster doctors ⇒ more
 * completions ⇒ a lower rate ⇒ every estimate drops on the next board tick).
 *
 * NO measured pace yet (completedRecent === 0): we can't measure, so fall back to
 * a default consult length spread across the present doctors (DEFAULT_CONSULT_MIN
 * / presentCount) — more doctors on the floor ⇒ a lower opening estimate. Fail
 * SANE, never divide-by-zero. Clamped to [MIN_RATE_MIN, MAX_RATE_MIN].
 */
export function serviceRate(completedRecent, presentCount, opts = {}) {
  const windowMin = num(opts.windowMin) || SERVICE_WINDOW_MIN;
  const doctors = Math.max(1, Math.floor(num(presentCount) || 0));
  const done = Math.max(0, Math.floor(num(completedRecent) || 0));
  if (done <= 0) return clampRate(DEFAULT_CONSULT_MIN / doctors);
  return clampRate(windowMin / done);
}

/**
 * estimateWait(position, rate) — §6. Minutes a patient at `position` (people
 * ahead of them in the ordered queue, 0-based) should expect to wait, given the
 * per-position `rate` from serviceRate. Floored at 0, capped at MAX_WAIT_MIN.
 */
export function estimateWait(position, rate) {
  const pos = Math.max(0, Math.floor(num(position) || 0));
  const r = Number.isFinite(num(rate)) && num(rate) > 0 ? num(rate) : DEFAULT_CONSULT_MIN;
  return clampWait(pos * r);
}

/** UI helper: a "~Xm" wait string (or '' for 0/unknown). English; callers add Hindi. */
export function waitLabel(minutes) {
  const m = num(minutes);
  if (!Number.isFinite(m) || m <= 0) return '';
  return `~${Math.round(m)}m`;
}

// Test surface for node/vitest harnesses AND the finalize-Bot parity check.
export const __queueEngineTest = {
  OPD_QUEUE_CLASS_EXT, OPD_ON_DUTY_EXT, QUEUE_CLASS, QUEUE_CLASS_RANK,
  PRESENCE_WINDOW_MS, DUTY_MAX_AGE_MS, SERVICE_WINDOW_MIN, DEFAULT_CONSULT_MIN,
  MIN_RATE_MIN, MAX_RATE_MIN, MAX_WAIT_MIN,
  readOnDuty, buildOnDutyExtension, derivePresence, roomFromRole, presentDoctorIds,
  readQueueClass, buildQueueClassExtension, queueOrder,
  serviceRate, estimateWait, waitLabel,
};
